import { useEffect, useState } from 'react'
import ResellerShell from './ResellerShell'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Custom Domain — Phase 8.
 *
 * Reseller enters a hostname they own (e.g. travel.yourbrand.com), points
 * a CNAME at our customers hostname, and we issue a Let's Encrypt cert
 * on-demand the first time a request hits Caddy.
 *
 * UI states:
 *   none         → input + "claim" button
 *   pending_dns  → waiting for the customer to update DNS
 *   pending_cert → DNS resolved correctly, awaiting first request
 *   live         → fully working
 *   failed       → DNS lookup errored, prompt to retry
 */
export default function CustomDomain() {
  const { t } = useLanguage()
  const [data, setData] = useState(null)
  const [hostname, setHostname] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  function reload() {
    setLoading(true); setErr(null)
    tournamentApi.get('/portal/reseller/domain')
      .then(r => { setData(r.data); setHostname(r.data.customDomain || '') })
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }
  useEffect(reload, [])

  async function claim() {
    setErr(null); setSubmitting(true)
    try {
      const { data } = await tournamentApi.put('/portal/reseller/domain', { customDomain: hostname })
      setData(d => ({ ...d, customDomain: data.customDomain, status: data.status }))
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setSubmitting(false) }
  }
  async function recheck() {
    setSubmitting(true)
    try {
      const { data } = await tournamentApi.post('/portal/reseller/domain/check')
      setData(d => ({ ...d, status: data.status }))
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setSubmitting(false) }
  }
  async function remove() {
    if (!confirm(t('sp_remove_custom_domain_confirm', 'Remove your custom domain? Customers using it will fall back to the default subdomain.'))) return
    try { await tournamentApi.delete('/portal/reseller/domain'); reload() }
    catch (e) { alert(e?.response?.data?.message || e.message) }
  }

  if (loading) return <ResellerShell title={t('sp_dash_row_custom_domain', 'Custom domain')}><div style={{ color: '#64748b' }}>{t('sp_loading_short', 'Loading…')}</div></ResellerShell>

  return (
    <ResellerShell title={t('sp_dash_row_custom_domain', 'Custom domain')}>
      <p style={{ color: '#64748b', marginTop: 0, marginBottom: 24, maxWidth: 720, fontSize: 14 }}>
        {t('sp_custom_domain_intro', 'Use a subdomain on your own website (e.g. travel.yourbrand.com) for the customer iframe + my-trip portal, instead of the default {subdomain}. Self-serve via DNS — no human required on our end.', { subdomain: data?.defaultSubdomain || t('sp_default_subdomain_fallback', 'subdomain') })}
      </p>

      {err && <div style={errorBox}>{err}</div>}

      <Section title={t('sp_your_domain', 'Your domain')}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text" value={hostname} onChange={e => setHostname(e.target.value)}
            placeholder={t('sp_domain_placeholder', 'travel.yourbrand.com')}
            style={{ ...inp, flex: 1, fontFamily: 'monospace' }}
            disabled={!!data?.customDomain}
          />
          {!data?.customDomain && <button onClick={claim} disabled={submitting || !hostname.trim()} style={{ ...primaryBtn, opacity: (submitting || !hostname.trim()) ? 0.6 : 1 }}>{submitting ? t('sp_claiming', 'Claiming…') : t('sp_claim_domain', 'Claim domain')}</button>}
        </div>

        {data?.customDomain && (
          <>
            <StatusPanel status={data.status} customDomain={data.customDomain} cnameTarget={data.cnameTarget} />
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={recheck} disabled={submitting} style={secondaryBtn}>{submitting ? t('sp_checking', 'Checking…') : t('sp_recheck_dns_now', 'Re-check DNS now')}</button>
              <button onClick={remove} style={{ ...secondaryBtn, color: '#dc2626', borderColor: '#fecaca' }}>{t('sp_remove_domain', 'Remove domain')}</button>
            </div>
          </>
        )}
      </Section>

      <Section title={t('sp_setup_instructions', 'Setup instructions')}>
        <ol style={{ color: '#475569', fontSize: 14, lineHeight: 1.7, paddingLeft: 20, margin: 0 }}>
          <li>{t('sp_dns_step_pick_subdomain', 'Pick a subdomain you control (e.g. travel.yourbrand.com) and claim it above.')}</li>
          <li>{t('sp_dns_step_login_provider', 'Log into your DNS provider (GoDaddy, Cloudflare, Namecheap, etc.).')}</li>
          <li>{t('sp_dns_step_create_cname', 'Create a CNAME record:')}
            <pre style={code}>{`Type:  CNAME
Name:  ${data?.customDomain ? data.customDomain.split('.')[0] : t('sp_dns_subdomain_placeholder', '<your subdomain>')}
Value: ${data?.cnameTarget || 'customers.integolf.com'}`}</pre>
          </li>
          <li>{t('sp_dns_step_wait_propagate', 'Wait 1–10 minutes for DNS to propagate, then click "Re-check DNS now" above.')}</li>
          <li>{t('sp_dns_step_live_iframe', 'Once status reads Live, your iframe URL becomes https://{domain}/embed/reseller/<key> automatically.', { domain: data?.customDomain || 'travel.yourbrand.com' })}</li>
        </ol>
        <div style={{ marginTop: 16, padding: 12, background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 6, fontSize: 13, color: '#854d0e' }}>
          {t('sp_cloudflare_warning', "If you use Cloudflare: set the proxy status to DNS only (grey cloud, not orange). Otherwise Cloudflare wraps the connection and our cert can't be issued.")}
        </div>
      </Section>
    </ResellerShell>
  )
}

function StatusPanel({ status, customDomain, cnameTarget }) {
  const { t } = useLanguage()
  const labels = {
    none:         { color: '#64748b', label: t('sp_status_not_configured', 'Not configured'), detail: '' },
    pending_dns:  { color: '#92400e', label: t('sp_status_waiting_dns', 'Waiting for DNS'), detail: t('sp_status_waiting_dns_detail', 'Create a CNAME pointing {domain} at {target}.', { domain: customDomain, target: cnameTarget }) },
    pending_cert: { color: '#1e40af', label: t('sp_status_issuing_cert', 'DNS detected — issuing certificate'), detail: t('sp_status_issuing_cert_detail', "On the first visit, Let's Encrypt issues the SSL cert. Visit your domain to trigger it.") },
    live:         { color: '#16a34a', label: t('sp_status_live', 'Live'), detail: t('sp_status_live_detail', 'Your domain is fully active.') },
    failed:       { color: '#dc2626', label: t('sp_status_dns_failed', 'DNS lookup failed'), detail: t('sp_status_dns_failed_detail', 'Verify the CNAME exists and re-check below.') },
  }
  const s = labels[status] || labels.none
  return (
    <div style={{ marginTop: 12, padding: 14, background: '#fff', border: `1px solid ${s.color}33`, borderLeft: `4px solid ${s.color}`, borderRadius: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: s.color, marginBottom: 4 }}>{s.label.toUpperCase()}</div>
      <div style={{ fontSize: 13, color: '#475569' }}>{s.detail}</div>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 16 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#0f172a' }}>{title}</h3>
      {children}
    </div>
  )
}

const inp = { padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const primaryBtn = { padding: '10px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '10px 14px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }
const code = { background: '#0f172a', color: '#e2e8f0', padding: 10, borderRadius: 6, fontSize: 12, marginTop: 8 }
